import { AuthService } from '../../services/auth/auth.service';
import { inject, Component, OnInit } from '@angular/core';
import { PendingProductChange } from '../../models/pending-product-change.model';

/** One reviewable field change from the scan payload (PascalCase in the JSON). */
export interface ProductFieldDiff {
  field: string;
  before: string | null;
  after: string | null;
}
import { PendingProductChangeService } from '../../services/pending-product-change.service';
import { UserRoleService } from '../../services/user-role.service';

@Component({
  selector: 'app-pending-product-changes',
  templateUrl: './pending-product-changes.component.html',
  styleUrls: ['./pending-product-changes.component.css'],
  standalone: false
})
export class PendingProductChangesComponent implements OnInit {
  readonly staffAuth = inject(AuthService);
  readonly pageSize = PendingProductChangeService.PAGE_SIZE;

  isLoading = false;
  changes: PendingProductChange[] = [];
  page = 1;
  totalCount = 0;
  totalPages = 1;
  rejectionDialogOpen = false;
  rejectionReason = '';
  changeToReject: PendingProductChange | null = null;
  scanning = false;
  /** Resolved display names for submitter user ids, shared across refreshes. */
  readonly userNames = new Map<string, string>();
  private readonly resolvingUserIds = new Set<string>();
  scanSummary = '';
  scanDetails: string[] = [];
  errorMessage = '';
  /** Change whose before/after detail row is expanded. */
  expandedChangeId: string | null = null;

  constructor(
    private service: PendingProductChangeService,
    private userRoleService: UserRoleService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  setPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.page = page;
    this.load();
  }

  get rangeLabel(): string {
    if (!this.totalCount) {
      return '0';
    }
    const start = (this.page - 1) * this.pageSize + 1;
    const end = Math.min(this.page * this.pageSize, this.totalCount);
    return `${start}–${end} of ${this.totalCount}`;
  }

  private load() {
    this.isLoading = true;
    this.service.getPendingChanges(this.page).subscribe({
      next: result => {
        this.isLoading = false;
        this.changes = result?.data ?? [];
        this.totalCount = result?.totalCount ?? 0;
        this.totalPages = Math.max(result?.totalPages ?? 1, 1);
        // Approving the last row of the last page shrinks the list; snap back.
        if (this.page > this.totalPages) {
          this.page = this.totalPages;
          this.load();
          return;
        }
        this.resolveSubmitterNames(this.changes);
      },
      error: err => {
        this.isLoading = false;
        this.changes = [];
        this.errorMessage = err?.message || 'Loading pending changes failed.';
      }
    });
  }

  /**
   * SubmittedBy is the tenant Items.UpdatedBy/CreatedBy user id. Resolve the
   * page's distinct ids to display names; a name that resolves also becomes a
   * link to /profile/:userId. Ids that fail to resolve (or aren't ids at all)
   * render as-is, unlinked.
   */
  private resolveSubmitterNames(changes: PendingProductChange[]) {
    const guid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const ids = new Set(
      changes
        .map(c => c.submittedBy)
        .filter((id): id is string =>
          !!id && guid.test(id) && !this.userNames.has(id) && !this.resolvingUserIds.has(id))
    );
    ids.forEach(id => {
      this.resolvingUserIds.add(id);
      this.userRoleService.getUserById(id).subscribe({
        next: user => {
          this.resolvingUserIds.delete(id);
          const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email;
          if (name) {
            this.userNames.set(id, name);
          }
        },
        error: () => this.resolvingUserIds.delete(id)
      });
    });
  }

  toggleDetails(change: PendingProductChange) {
    this.expandedChangeId = this.expandedChangeId === change.id ? null : change.id;
  }

  /**
   * Field-level before/after captured at scan time. The payload is serialized
   * PascalCase by the backend; rows queued before diffs existed return [] and
   * the template falls back to the summary text.
   */
  getDiffs(change: PendingProductChange): ProductFieldDiff[] {
    const raw = change.details?.Diffs ?? change.details?.diffs;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((d: any) => ({
      field: d?.Field ?? d?.field ?? '',
      before: d?.Before ?? d?.before ?? null,
      after: d?.After ?? d?.after ?? null
    }));
  }

  approve(change: PendingProductChange) {
    if (!this.staffAuth.can('Marketplace.ProductChanges.Moderate')) return;
    this.errorMessage = '';
    this.service.approveChange(change.id).subscribe({
      next: () => this.load(),
      error: err => (this.errorMessage = err?.message || 'Approving the change failed.')
    });
  }

  reject(change: PendingProductChange) {
    if (!this.staffAuth.can('Marketplace.ProductChanges.Moderate')) return;
    this.changeToReject = change;
    this.rejectionDialogOpen = true;
    this.rejectionReason = '';
  }

  confirmReject() {
    if (!this.staffAuth.can('Marketplace.ProductChanges.Moderate')) return;
    if (this.changeToReject && this.rejectionReason.trim()) {
      this.errorMessage = '';
      this.service.rejectChange(this.changeToReject.id, this.rejectionReason.trim()).subscribe({
        next: () => this.load(),
        error: err => (this.errorMessage = err?.message || 'Rejecting the change failed.')
      });
      this.cancelReject();
    }
  }

  cancelReject() {
    this.rejectionDialogOpen = false;
    this.changeToReject = null;
    this.rejectionReason = '';
  }

  runScan() {
    if (!this.staffAuth.can('Marketplace.ProductChanges.Moderate')) return;
    this.scanning = true;
    this.scanSummary = '';
    this.scanDetails = [];
    this.errorMessage = '';
    this.service.runScan().subscribe({
      next: result => {
        this.scanning = false;
        const skipped = result.skipped ?? [];
        const errors = result.errors ?? [];
        this.scanSummary = `Scanned ${result.businessesScanned} seller(s), queued ${result.changesQueued} change(s)` +
          (skipped.length ? `, skipped ${skipped.length}` : '') +
          (errors.length ? `, ${errors.length} tenant error(s)` : '') + '.';
        // Show why sellers were skipped/failed so "queued 0" is never mistaken for "no changes".
        this.scanDetails = [...errors.map(e => `Error — ${e}`), ...skipped.map(s => `Skipped — ${s}`)];
        this.page = 1;
        this.load();
      },
      error: err => {
        this.scanning = false;
        this.errorMessage = err?.message || 'The catalog scan failed.';
      }
    });
  }
}
